import { memo, useState } from 'react';

import { Button, PageTitle } from '@dec/shared';
import { ButtonAction } from '@dec/shared';
import { HookFormResolver, TextArea } from '@dec/shared';
import { type HookFormMethods } from '@dec/shared';
import { BtnGroup, BtnToolbar, Col, Row } from '@dec/shared';
import { AutoWizard } from '@dec/shared';
import { Card, Tab, Tabs } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import DateParser from 'utils/date';

import { useCatalogValueList } from '../../configuration/catalogs/hooks/useCatalogValueList';
import { catalogName } from '../../configuration/catalogs/models/Catalog';
import { useGetEnabledRegistrationFields } from '../../Parameters/TaxpayerFieldRegistration/services/hooks';
import {
  useGetFilesDefinitionTaxpayerRequest,
  useGetReportByOperations,
  useGetTaxByOperations,
  useGetTaxpayerRequest,
  useTaxpayerRequestCapabilities
} from '../TaxpayerRequest/hooks';
import { MyContext } from '../TaxpayerRequest/index';
import { Contact } from '../TaxpayerRequest/steps/contact';
import { Documents } from '../TaxpayerRequest/steps/documents';
import { GeneralData } from '../TaxpayerRequest/steps/general-data';
import { Operation } from '../TaxpayerRequest/steps/operations';
import { ReportVector } from '../TaxpayerRequest/steps/report-vector';
import { FormTax } from '../TaxpayerRequest/steps/tax';
import { TaxVector } from '../TaxpayerRequest/steps/tax-vector';
import { label } from '../TaxpayerRequest/translation';
import { type GeneralTaxpayer, type Steps } from '../TaxpayerRequest/types';
import { type TaxpayerRegistrationFieldConfiguration } from '../TaxpayerRequestEvaluation/types';

interface WizardProps {
  conf: TaxpayerRegistrationFieldConfiguration;
  completeData: Steps;
}

const StepWizard = AutoWizard;

export const Wizard = ({ conf, completeData: completeData }: WizardProps) => {
  const { data: countries } = useCatalogValueList(catalogName.countries);
  const { t } = useTranslation();
  const {
    data: taxReturnCurrencies,
    isLoading: loadingTaxReturnCurrency,
    error: errorTaxReturnCurrency
  } = useCatalogValueList(catalogName.taxReturnCurrencies);

  const { data: personTypes } = useCatalogValueList(catalogName.personTypes);

  const [{ data: dataOperations, loading, error }] = useGetTaxByOperations(
    completeData.operations?.map((x) => x.id) as Array<string>,
    completeData.operations?.length > 0,
    true
  );
  const [{ data: reportOperation, loading: reportLoading, error: reportError }] =
    useGetReportByOperations(
      completeData.operations?.map((x) => x.id) as Array<string>,
      completeData.operations?.length > 0,
      true
    );
  const { data: definitions } = useGetFilesDefinitionTaxpayerRequest(true);

  const {
    isLoading: loadingDefinition,
    data: contactFilesDefinitions,
    error: errorDefinition
  } = useCatalogValueList('contact-files-definition');

  const {
    loading: loadingCapabilities,
    error: capabilitiesError,
    reportsEnabled,
    attachmentsEnabled
  } = useTaxpayerRequestCapabilities(true);
  if (
    loading ||
    loadingTaxReturnCurrency ||
    errorTaxReturnCurrency ||
    error ||
    loadingCapabilities ||
    capabilitiesError ||
    reportLoading ||
    reportError ||
    loadingDefinition ||
    errorDefinition
  ) {
    return <></>;
  }
  const [contactDefinition] = (contactFilesDefinitions ?? []).filter(
    (x) => x.additionalFields.enable
  );

  return (
    <MyContext.Provider value={conf}>
      <StepWizard
        transitions={{}}
        isLazyMount={false}
        nav={
          <Tabs
            defaultActiveKey="generalData"
            id="uncontrolled-tab"
            className="nav-fill"
            mountOnEnter={true}
            unmountOnExit={true}
          >
            <Tab eventKey="generalData" title={label(t, 'generalData')}>
              <GeneralData
                currentData={completeData.general as GeneralTaxpayer}
                personTypes={personTypes ?? []}
                countries={countries ?? []}
                readonly={true}
                onSubmit={() => {}}
              />
            </Tab>
            <Tab eventKey="tax" title={label(t, 'tax')}>
              <FormTax
                taxReturnCurrencies={taxReturnCurrencies ?? []}
                onSubmit={() => {}}
                defaultValues={completeData.tax}
                readonly={true}
              />
            </Tab>
            <Tab eventKey="contacts" title={label(t, 'contacts')}>
              <Contact onSubmit={() => {}} contacts={completeData.contacts} readonly={true} />
            </Tab>
            <Tab eventKey="operations" title={label(t, 'operations')}>
              <Operation onSubmit={() => {}} operations={completeData.operations} readonly={true} />
            </Tab>
            <Tab eventKey="taxVector" title={label(t, 'taxVector')}>
              <TaxVector
                onSubmit={() => {}}
                taxes={dataOperations || []}
                data={completeData.taxes}
                readonly={true}
              />
            </Tab>
            {reportsEnabled && (
              <Tab eventKey="reportVector" title={label(t, 'reportVector')}>
                <ReportVector
                  onSubmit={() => {}}
                  reports={reportOperation || []}
                  data={completeData.reports}
                  readonly={true}
                />
              </Tab>
            )}
            {attachmentsEnabled && (
              <Tab eventKey="documents" title={label(t, 'documents')}>
                <Documents
                  contactsModels={completeData.contacts}
                  contactDefinition={contactDefinition}
                  onSubmit={() => {}}
                  definitions={definitions ?? []}
                  data={completeData.documents}
                  readonly={true}
                />
              </Tab>
            )}
          </Tabs>
        }
      />
    </MyContext.Provider>
  );
};

interface Props {}

const ControlText = ({ title, value }: { title: string; value: string }) => {
  return (
    <>
      <h6 className="card-subtitle text-muted">{title}</h6>
      <h4 className="card-title">{value}</h4>
    </>
  );
};

const HeaderDetail = ({ dataReques }: { dataReques: any }) => {
  const { t, i18n } = useTranslation();
  return (
    <>
      <div className="card mt-2">
        <div className="card-body p-3">
          <Row>
            <Col>
              <ControlText title={t('config.tin')} value={dataReques.taxpayer?.nit} />
            </Col>
            <Col>
              <ControlText
                title={t('taxpayer-request-val.label.taxpayer')}
                value={dataReques.taxpayerName}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <ControlText
                title={t('taxpayer-request-val.label.type')}
                value={t(`common.taxpayer-request-types.${dataReques.requestType}`)}
              />
            </Col>
            <Col>
              <ControlText
                title={t('taxpayer-request-val.label.createdDate')}
                value={DateParser.toString(dataReques.createdDate)}
              />
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
};

export const TaxpayerHistoryDetail = memo((props: Props) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { t, i18n } = useTranslation();
  const [periodicityError, setPeriodicityErro] = useState(false);
  const [{ data, loading, error, response }] = useGetEnabledRegistrationFields();
  const { id } = useParams<Record<string, string>>();
  const [{ data: dataReques, loading: loadingRequest, error: errorRequest, response: rspRequest }] =
    useGetTaxpayerRequest(id ?? '');

  const navigate = useNavigate();

  if (loadingRequest || errorRequest || loading || error) {
    return <></>;
  }
  return (
    <div>
      <PageTitle title={t('taxpayer-request.label.queryOfRequest')} />

      <HeaderDetail dataReques={dataReques} />

      {dataReques?.recordData && dataReques?.requestType !== 'unsubscribe' ? (
        <Card>
          <Card.Body>
            <Wizard conf={{ fields: data || [] }} completeData={dataReques.recordData} />
          </Card.Body>
        </Card>
      ) : (
        <HookFormResolver
          initialValues={{ reason: dataReques?.recordData?.reason }}
          onSubmit={(data: any) => {}}
          validationMode="onSubmit"
        >
          {({ formState: { errors }, reset, setValue, control }: HookFormMethods) => {
            return (
              <>
                <Row>
                  <Col>
                    <TextArea
                      readOnly={true}
                      name="reason"
                      label={t('taxpayer-request-val.label.reason')}
                    />
                  </Col>
                </Row>
              </>
            );
          }}
        </HookFormResolver>
      )}
      {periodicityError && (
        <p className="text-danger">{t('taxpayer-request-val.message.periodictyOutOfDate')}</p>
      )}
      <BtnToolbar>
        <BtnGroup>
          <>
            <Button
              action={ButtonAction.cancel}
              variant="secondary"
              onClick={() => {
                navigate(-1);
                //.push(`/taxpayer/history?id=${dataReques.taxpayerId}`
              }}
            />
          </>
        </BtnGroup>
        <BtnGroup>
          <></>
        </BtnGroup>
      </BtnToolbar>
    </div>
  );
});

TaxpayerHistoryDetail.displayName = 'TaxpayerHistoryDetail';
